import {
  IssueType,
  SOURCE_URL_PARAMS_KEYS,
  SourceEntityType,
  SourceAppType,
} from '@issuemanager/constants';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  IntegrationSourceUrlParams,
  integrationSourceUrlParamsSchema,
} from '../schemas/create-issue-schema';

/**
 * @desc Extracts the source parameters from the URL and returns them as an IssueSourceDto object.
 * @returns IssueSourceDto
 * @example http://localhost:4200/issues/create?sourceApp=audit&sourceEntityType=audit&sourceEntityName=AuditName&type=audit-finding&sourceEntityId=123&sourceEntityLink=https://example.com&callbackUrl=https://example.com
 */

type UseSourceParams = {
  sourceInfo: IntegrationSourceUrlParams | undefined;
  isSourceError: boolean;
  isSourceExpected: boolean;
};

function getUrlParamKeys(searchParams: URLSearchParams) {
  return searchParams
    .toString()
    .split('&')
    .map((param) => param.split('=')[0])
    .filter(Boolean);
}

export const useSourceParams = (): UseSourceParams => {
  const [searchParams] = useSearchParams();
  const [isSourceError, setIsSourceError] = useState(false);
  const [sourceInfo, setSourceInfo] = useState<
    IntegrationSourceUrlParams | undefined
  >(undefined);

  const sourceEntityId = searchParams.get('sourceEntityId');
  const sourceEntityName = searchParams.get('sourceEntityName');
  const sourceEntityLink = searchParams.get('sourceEntityLink');
  const sourceEntityType = searchParams.get('sourceEntityType');
  const sourceApp = searchParams.get('sourceApp');
  const type = searchParams.get('type');
  const callbackUrl = searchParams.get('callbackUrl');

  const params = useMemo(
    () => ({
      sourceEntityId,
      sourceEntityName,
      sourceEntityLink,
      sourceEntityType: sourceEntityType as SourceEntityType,
      sourceApp: sourceApp as SourceAppType,
      type: type as IssueType,
      callbackUrl,
    }),
    [
      callbackUrl,
      sourceApp,
      sourceEntityId,
      sourceEntityLink,
      sourceEntityName,
      sourceEntityType,
      type,
    ],
  );

  const whitelistSourceUrlParamKeys =
    SOURCE_URL_PARAMS_KEYS as readonly string[];

  const paramValidation = useMemo(() => {
    const unauthorizedParams = getUrlParamKeys(searchParams).filter(
      (param) => !whitelistSourceUrlParamKeys.includes(param),
    );

    const duplicateParams: string[] = [];
    whitelistSourceUrlParamKeys.forEach((param) => {
      const allValues = searchParams.getAll(param);
      if (allValues.length > 1) {
        duplicateParams.push(param);
      }
    });

    const hasUnauthorizedParams = unauthorizedParams.length > 0;
    const hasDuplicateParams = duplicateParams.length > 0;

    if (hasUnauthorizedParams) {
      console.error('Unauthorized parameters detected:', unauthorizedParams);
    }
    if (hasDuplicateParams) {
      console.error('Duplicate parameters detected:', duplicateParams);
    }

    return {
      hasUnauthorizedParams,
      hasDuplicateParams,
      unauthorizedParams,
      duplicateParams,
    };
  }, [searchParams, whitelistSourceUrlParamKeys]);

  const hasInvalidParams = useMemo(
    () =>
      !integrationSourceUrlParamsSchema.safeParse(params).success ||
      paramValidation.hasUnauthorizedParams ||
      paramValidation.hasDuplicateParams,
    [params, paramValidation],
  );

  const isSourceExpected = useMemo(
    () =>
      whitelistSourceUrlParamKeys.some((param) =>
        getUrlParamKeys(searchParams).includes(param),
      ),
    [searchParams],
  );

  useEffect(() => {
    if (!isSourceExpected) {
      setIsSourceError(false);
      setSourceInfo(undefined);
      return;
    }

    if (hasInvalidParams) {
      // eslint-disable-next-line no-console
      console.error('Invalid integration source parameters');
      setIsSourceError(true);
    } else {
      setIsSourceError(false);
    }

    setSourceInfo(params as IntegrationSourceUrlParams);
  }, [hasInvalidParams, params, isSourceExpected]);

  return {
    sourceInfo,
    isSourceError,
    isSourceExpected,
  };
};
